import React from 'react';
import { Input } from './UI/Input';
import { Toggle } from './UI/Toggle';
import { useExpediteStore } from '../store/useExpediteStore';
import { Formik, Form } from 'formik';
import { object, string, number } from 'yup';
import Card from './UI/Card';
import {Textarea} from './UI/Textarea';
import {MaskedInput} from './UI/MaskedInput';
import ImageUploader from './ImageUploader';

const ExpediteForm = () => {
  const {
    name,
    email,
    position,
    extension,
    cellPhone,
    disclaimer,
    updatesEmail,
    linkedin,
    pictureUrl,
    hasLoadbaseBanner,
    hasWarehouse411Banner,
    setField,
  } = useExpediteStore();

  const phoneNumberRegex:RegExp = /^\(\d{3}\)\s\d{3}-\d{4}$/;

  const validationSchema = object().shape({
    name: string()
        .label('Name')
        .required()
        .trim(),
    email: string()
        .label('E-mail')
        .required()
        .email()
        .trim(),
    position: string()
        .label('Position')
        .required()
        .trim(),
    updatesEmail: string()
      .label('Updates email')
      .email()
      .trim(),
    extension: number().label('Extension'),
    cellPhone: string()
      .label('Cell Phone Number')
      .notRequired()
      .test(
        'is-valid-phone',
        'Phone number must be in format (XXX) XXX-XXXX',
        (value: any) => {
          if (!value) return true;
          return phoneNumberRegex.test(value);
        }
      ),
    linkedin: string().label('LinkedIn').url(),
    disclaimer:
      string()
        .label('Disclaimer')
        .trim(),
  })

  return (
    <Card title='Signature Information'>
      <Formik
        initialValues={{
          name,
          email,
          position,
          extension,
          cellPhone,
          disclaimer,
          hasLoadbaseBanner,
          hasWarehouse411Banner,
          updatesEmail,
          linkedin,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          Object.entries(values).forEach(([key, value]) => {
            setField(key as any, value);
          });
        }}
      >
        {({ values, handleChange, setFieldValue, isValid }) => {
          const handleChangeAndSync = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            handleChange(e);
            setField(e.target.name as any, e.target.value);
          };

          const handleBooleanChange = (key: keyof typeof values, value: boolean) => {
            setFieldValue(key, value);
            setField(key, value);
          };

          return (
            <Form className="flex flex-col space-y-4">
              <div className='flex gap-6'>
                <div className='w-1/4'>
                  <ImageUploader pictureUrl={pictureUrl} setField={(value: string) => setField('pictureUrl', value)} />
                </div>
                <div className='w-3/4'>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Name" name="name" value={values.name} onChange={handleChangeAndSync} />
                    <Input label="Email" name="email" value={values.email} onChange={handleChangeAndSync} />
                    <Input label="Extension" name="extension" value={values.extension} onChange={handleChangeAndSync} />
                    <MaskedInput label="Cell Phone" name="cellPhone" mask="(999) 999-9999" onChange={handleChangeAndSync} />
                    <Input label="Position" name="position" value={values.position} onChange={handleChangeAndSync} />
                    <Input label="Linkedin" name="linkedin" value={values.linkedin} onChange={handleChangeAndSync} />
                  </div>
                </div>
              </div>

              <Textarea
                label="Disclaimer"
                name="disclaimer"
                value={values.disclaimer}
                onChange={handleChangeAndSync}
              />

              <div className="grid grid-cols-3 gap-6">
                <Input label="Updates Email" name="updatesEmail" value={values.updatesEmail} onChange={handleChangeAndSync} />
                <div className='flex justify-center mt-6'>
                  <Toggle
                    label="Loadbase Banner"
                    value={values.hasLoadbaseBanner}
                    onChange={(val: boolean) => handleBooleanChange('hasLoadbaseBanner', val)}
                  />
                </div>
                <div className='flex justify-center mt-6'>
                  <Toggle
                    label="WH411 Banner"
                    value={values.hasWarehouse411Banner}
                    onChange={(val: boolean) => handleBooleanChange('hasWarehouse411Banner', val)}
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};

export default ExpediteForm;
